import { create } from "zustand";

type MachineStore = {
  allIsPlaying: boolean;
  binauralMachines: Record<string, boolean>;
  dualBinauralMachines: Record<string, boolean>;
  isochronicMachines: Record<string, boolean>;
  metronomes: Record<string, boolean>;
  samplers: Record<string, boolean>;
  gameSamplers?: Record<string, boolean>;
  yStimMachines?: Record<string, boolean>;
  noiseMachines?: Record<string, boolean>;
  setPlayingStatus: (machineType: string, machineName: string, status: boolean) => void;
  turnEverythingOff: () => void;
  toggleAll: () => void;
  togglePlayingStatus: (
    type:
      | "binauralMachines"
      | "dualBinauralMachines"
      | "metronomes"
      | "samplers"
      | "isochronicMachines"
      | "yStimMachines"
      | "noiseMachines",
    name: string
  ) => void;
  addMachine: (
    type:
      | "binauralMachines"
      | "dualBinauralMachines"
      | "metronomes"
      | "samplers"
      | "isochronicMachines"
      | "yStimMachines"
      | "noiseMachines",
    name: string
  ) => void;
};

export const useMachineStore = create<MachineStore>((set) => ({
  allIsPlaying: false,
  binauralMachines: {},
  dualBinauralMachines: {},
  metronomes: {},
  samplers: {},
  isochronicMachines: {},
  yStimMachines: {},
  noiseMachines: {},
  setPlayingStatus: (machineType: string, machineName: string, status: boolean) =>
    set((state) => {
      return {
        ...state,
        [machineType]: {
          ...state[machineType],
          [machineName]: status,
        },
      };
    }),
  turnEverythingOff: () =>
    set(() => {
      const turnOffAllMachines = (machines: Record<string, boolean>) => {
        const newMachines = { ...machines };
        for (let name in newMachines) {
          newMachines[name] = false;
        }
        return newMachines;
      };
      return {
        allIsPlaying: false,
        binauralMachines: turnOffAllMachines({}),
        dualBinauralMachines: turnOffAllMachines({}),
        metronomes: turnOffAllMachines({}),
        samplers: turnOffAllMachines({}),
        isochronicMachines: turnOffAllMachines({}),
        yStimMachines: turnOffAllMachines({}),
        noiseMachines: turnOffAllMachines({}),
      };
    }),
  toggleAll: () =>
    set((state) => {
      const newIsPlaying = !state.allIsPlaying;
      const toggleAllMachines = (machines: Record<string, boolean>) => {
        const newMachines = { ...machines };
        for (let name in newMachines) {
          newMachines[name] = newIsPlaying;
        }
        return newMachines;
      };
      return {
        allIsPlaying: newIsPlaying,
        binauralMachines: toggleAllMachines(state.binauralMachines),
        dualBinauralMachines: toggleAllMachines(state.dualBinauralMachines),
        metronomes: toggleAllMachines(state.metronomes),
        samplers: toggleAllMachines(state.samplers),
        isochronicMachines: toggleAllMachines(state.isochronicMachines),
        yStimMachines: state.yStimMachines ? toggleAllMachines(state.yStimMachines) : {},
        noiseMachines: state.noiseMachines ? toggleAllMachines(state.noiseMachines) : {},
      };
    }),
  togglePlayingStatus: (type, name) =>
    set((state) => {
      const machines = { ...state[type] };
      machines[name] = !machines[name];
      return { [type]: machines };
    }),
  addMachine: (type, name) =>
    set((state) => ({
      ...state,
      [type]: {
        ...state[type],
        [name]: false,
      },
    })),
}));
